import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStravaAnalysis } from "@/hooks/useStravaAnalysis";

// Loading messages that rotate during analysis
const LOADING_MESSAGES = [
  "Crunching your miles...",
  "Calculating your dedication...",
  "Finding your runner personality...",
  "Analyzing your patterns...",
  "Almost there...",
];

interface StravaAnalysisSectionProps {
  onReconnectStrava?: () => void;
}

export function StravaAnalysisSection({ onReconnectStrava }: StravaAnalysisSectionProps) {
  const navigate = useNavigate();
  const {
    isLoading,
    isAnalyzing,
    error,
    noActivities,
    fetchAnalysis,
    runAnalysis,
  } = useStravaAnalysis();

  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

  // Check for existing analysis on mount - if exists, navigate to summary page
  useEffect(() => {
    fetchAnalysis().then((result) => {
      if (result) {
        navigate("/analysis-summary");
      }
    });
  }, [fetchAnalysis, navigate]);

  // Rotate loading messages during analysis
  useEffect(() => {
    if (!isAnalyzing) return;
    const interval = setInterval(() => {
      setLoadingMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [isAnalyzing]);

  // Navigate to welcome-runner page when user has no activities
  useEffect(() => {
    if (noActivities) {
      navigate("/welcome-runner");
    }
  }, [noActivities, navigate]);

  // Handle analyze click - navigate to summary on success
  const handleAnalyze = async () => {
    const result = await runAnalysis();
    if (result) {
      navigate("/analysis-summary");
    }
  };

  // Loading state (initial fetch)
  if (isLoading) {
    return <AnalysisSkeleton />;
  }

  // Error states - check BEFORE analyzing state so errors show properly
  if (error?.code === "TOKEN_EXPIRED" || error?.code === "NO_STRAVA_CONNECTION") {
    return (
      <ErrorCard
        message={error.message}
        actionLabel="Reconnect Strava"
        onAction={onReconnectStrava}
      />
    );
  }

  // Analyzing state - show loading
  if (isAnalyzing) {
    return <AnalysisLoading message={LOADING_MESSAGES[loadingMessageIndex]} />;
  }

  // Default: show analyze button
  return <AnalyzeButton onClick={handleAnalyze} error={error} />;
}

// ============================================
// ANALYZE BUTTON
// ============================================

function AnalyzeButton({
  onClick,
  error,
}: {
  onClick: () => void;
  error: { code: string; message: string } | null;
}) {
  return (
    <div className="space-y-3">
      <button
        onClick={onClick}
        className="flex items-center gap-4 rounded-3xl bg-white p-4 text-left shadow-sm ring-1 ring-subtle-light dark:bg-subtle-dark dark:ring-0 w-full hover:opacity-90 transition-opacity"
      >
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 dark:bg-primary/20">
          <span className="material-symbols-outlined text-primary text-2xl">
            insights
          </span>
        </div>
        <div className="flex-1">
          <p className="font-bold text-content-light dark:text-content-dark">
            Analyze My Running
          </p>
          <p className="text-sm text-content-light/70 dark:text-content-dark/70">
            Discover your runner personality
          </p>
        </div>
        <span className="material-symbols-outlined text-content-light/50 dark:text-content-dark/50">
          chevron_right
        </span>
      </button>
      {error && (
        <p className="text-center text-sm text-red-500">{error.message}</p>
      )}
    </div>
  );
}

// ============================================
// ANALYSIS LOADING
// ============================================

function AnalysisLoading({ message }: { message: string }) {
  return (
    <div className="rounded-3xl bg-subtle-dark p-8 ring-1 ring-white/10">
      <div className="flex flex-col items-center text-center">
        {/* Animated spinner */}
        <div className="relative h-20 w-20">
          <div className="absolute inset-0 rounded-full border-4 border-primary/20"></div>
          <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
          <div className="absolute inset-2 rounded-full bg-primary/10 flex items-center justify-center">
            <span className="material-symbols-outlined text-primary text-2xl">
              directions_run
            </span>
          </div>
        </div>
        <p className="mt-6 font-bold text-white text-lg">{message}</p>
        <p className="mt-2 text-sm text-white/60">
          This usually takes about 10 seconds
        </p>
      </div>
    </div>
  );
}

// ============================================
// HELPER COMPONENTS
// ============================================

function AnalysisSkeleton() {
  return (
    <div className="rounded-3xl bg-subtle-dark p-6 ring-1 ring-white/10 animate-pulse">
      <div className="flex flex-col items-center">
        <div className="h-20 w-20 rounded-full bg-white/10"></div>
        <div className="h-6 w-40 rounded bg-white/10 mt-4"></div>
        <div className="h-4 w-56 rounded bg-white/10 mt-2"></div>
        <div className="grid grid-cols-2 gap-3 w-full mt-6">
          <div className="h-24 rounded-xl bg-white/10"></div>
          <div className="h-24 rounded-xl bg-white/10"></div>
        </div>
      </div>
    </div>
  );
}

function ErrorCard({
  message,
  actionLabel,
  onAction,
}: {
  message: string;
  actionLabel: string;
  onAction?: () => void;
}) {
  return (
    <div className="rounded-3xl bg-red-500/10 p-5 ring-1 ring-red-500/30">
      <div className="text-center">
        <span className="material-symbols-outlined text-red-400 text-3xl">
          error
        </span>
        <p className="mt-2 font-medium text-red-200">{message}</p>
        {onAction && (
          <button
            onClick={onAction}
            className="mt-4 rounded-full bg-red-500 px-6 py-2 text-sm font-bold text-white hover:bg-red-600 transition-colors"
          >
            {actionLabel}
          </button>
        )}
      </div>
    </div>
  );
}
